import React, { useMemo, useState } from "react"
import { REV_BERRI_ROUTE, formatKm, formatPercent } from "./revBerriRoute"
import { trackEvent } from "./analytics"

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://ahuncycle-backend.vercel.app"
const GAME_URL = "https://www.ahuncycle.com/jeux/velo"

function getIntersectionLabel(lastIntersection, routeProgress) {
  if (typeof lastIntersection === "string") {
    return lastIntersection
  }

  if (lastIntersection?.label) {
    return lastIntersection.label
  }

  if (lastIntersection?.nom) {
    return lastIntersection.nom
  }

  if (lastIntersection?.name) {
    return lastIntersection.name
  }

  if (lastIntersection?.id) {
    return lastIntersection.id
  }

  if (routeProgress?.currentSegment?.to?.label) {
    return routeProgress.currentSegment.to.label
  }

  if (routeProgress?.currentSegment?.to?.id) {
    return routeProgress.currentSegment.to.id
  }

  if (routeProgress?.currentSegment?.from?.label) {
    return routeProgress.currentSegment.from.label
  }

  if (routeProgress?.currentSegment?.from?.id) {
    return routeProgress.currentSegment.from.id
  }

  return "Gouin"
}

function getLevelLabel(level) {
  if (level === undefined || level === null || level === "") {
    return "inconnu"
  }

  return String(level)
}

export default function VeloShareScore({
  score,
  routeProgress,
  level,
  lastIntersection,
  playerName,
  highscoreRank,
  onReplay
}) {
  const [copied, setCopied] = useState(false)
  const [sharingFacebook, setSharingFacebook] = useState(false)
  const [shareError, setShareError] = useState("")

  const distanceKm = routeProgress?.distanceKm || 0
  const completionPercent = routeProgress?.completionPercent || 0
  const completedRoute = Boolean(routeProgress?.completedRoute)
  const displayPlayerName =
    typeof playerName === "string" && playerName.trim()
      ? playerName.trim()
      : "Cycliste anonyme"
  const displayHighscoreRank =
    Number(highscoreRank) >= 1
      ? Math.round(Number(highscoreRank))
      : null

  const intersectionLabel = useMemo(
    () => getIntersectionLabel(lastIntersection, routeProgress),
    [lastIntersection, routeProgress]
  )

  const levelLabel = useMemo(
    () => getLevelLabel(level),
    [level]
  )

  const shareText = useMemo(() => {
    const baseText = completedRoute
      ? "J'ai complété tout le parcours REV Berri jusqu'au parc René-Lévesque!"
      : "J'ai joué à REV Berri, le jeu de vélo d'Ahuncycle."

    return (
      `${baseText} ` +
      `Score: ${score || 0}. ` +
      `Cycliste: ${displayPlayerName}. ` +
      (displayHighscoreRank ? `Position: #${displayHighscoreRank}. ` : "") +
      `Distance: ${formatKm(distanceKm)}. ` +
      `Progression: ${formatPercent(completionPercent)}. ` +
      `Niveau: ${levelLabel}. ` +
      `Intersection: ${intersectionLabel}. ` +
      "Peux-tu me battre?"
    )
  }, [
    completedRoute,
    score,
    displayPlayerName,
    displayHighscoreRank,
    distanceKm,
    completionPercent,
    levelLabel,
    intersectionLabel
  ])

  const copyText = `${shareText}\n${GAME_URL}`

  async function createServerShare() {
    const response = await fetch(`${API_BASE_URL}/api/velo-shares`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        score: Number(score) || 0,
        distanceKm: Number(distanceKm.toFixed(2)),
        completionPercent: Math.round(completionPercent),
        completedRoute,
        level: Number(level) || 1,
        lastIntersection: intersectionLabel,
        playerName: displayPlayerName,
        highscoreRank: displayHighscoreRank
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error("Erreur création partage:", response.status, errorText)
      throw new Error("Erreur création partage")
    }

    const data = await response.json()

    if (!data?.shareUrl) {
      throw new Error("shareUrl manquant")
    }

    return data.shareUrl
  }

  async function handleFacebookShare() {
  if (sharingFacebook) return

  setSharingFacebook(true)
  setShareError("")

  trackEvent("score_share_click", {
    game_name: REV_BERRI_ROUTE.name,
    platform: "facebook",
    score,
    distance_km: Number(distanceKm.toFixed(2)),
    completion_percent: Math.round(completionPercent),
    completed_route: completedRoute,
    level,
    last_intersection: intersectionLabel,
    player_name: displayPlayerName,
    highscore_rank: displayHighscoreRank
  })

  /*
    Important :
    On ouvre une seule fenêtre immédiatement au clic.
    Ne pas mettre noopener/noreferrer ici, sinon on risque
    de ne plus pouvoir rediriger cette fenêtre vers Facebook.
  */
  const shareWindow = window.open(
    "about:blank",
    "rev-berri-facebook-share",
    "width=720,height=640"
  )

  if (shareWindow) {
    shareWindow.document.write(`
      <html>
        <body style="font-family: Arial, sans-serif; padding: 24px;">
          <p>Préparation du partage Facebook...</p>
        </body>
      </html>
    `)
    shareWindow.document.close()
  }

  try {
    const shareUrl = await createServerShare()

    const facebookUrl =
      "https://www.facebook.com/sharer/sharer.php?u=" +
      encodeURIComponent(shareUrl)

    if (shareWindow && !shareWindow.closed) {
      shareWindow.location.replace(facebookUrl)
    } else {
      window.open(
        facebookUrl,
        "rev-berri-facebook-share",
        "width=720,height=640"
      )
    }
  } catch (err) {
    console.error("Erreur partage Facebook:", err)
    setShareError("Impossible de créer le lien de partage.")

    if (shareWindow && !shareWindow.closed) {
      shareWindow.close()
    }
  } finally {
    setSharingFacebook(false)
  }
}

  async function handleCopy() {
    trackEvent("score_share_click", {
      game_name: REV_BERRI_ROUTE.name,
      platform: "copy",
      score,
      distance_km: Number(distanceKm.toFixed(2)),
      completion_percent: Math.round(completionPercent),
      completed_route: completedRoute,
      level,
      last_intersection: intersectionLabel
    })

    try {
      await navigator.clipboard.writeText(copyText)
      setCopied(true)
    } catch (err) {
      const input = document.createElement("textarea")

      input.value = copyText
      input.setAttribute("readonly", "")
      input.style.position = "fixed"
      input.style.opacity = "0"

      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)

      setCopied(true)
    }

    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="velo-share">
      <section className="velo-share__panel">
        <p className="velo-share__kicker">{REV_BERRI_ROUTE.subtitle}</p>
        <h2>{REV_BERRI_ROUTE.name}</h2>

        <div className="velo-share__stats">
          <div>
            <span>Nom</span>
            <strong>{displayPlayerName}</strong>
          </div>

          <div>
            <span>Position highscore</span>
            <strong>
              {displayHighscoreRank ? `#${displayHighscoreRank}` : "en calcul"}
            </strong>
          </div>

          <div>
            <span>Distance parcourue</span>
            <strong>{formatKm(distanceKm)}</strong>
          </div>

          <div>
            <span>Progression du parcours</span>
            <strong>{formatPercent(completionPercent)}</strong>
          </div>

          <div>
            <span>Score</span>
            <strong>{score || 0}</strong>
          </div>

          <div>
            <span>Niveau</span>
            <strong>{levelLabel}</strong>
          </div>

          <div>
            <span>Intersection</span>
            <strong>{intersectionLabel}</strong>
          </div>
        </div>

        <p className="velo-share__message">
          {completedRoute
            ? "Bravo! Tu as complété le parcours jusqu'au parc René-Lévesque."
            : "Peux-tu te rendre jusqu'au parc René-Lévesque?"}
        </p>

        <div className="velo-share__linkBox">
          <span>Texte de partage</span>
          <p>{shareText}</p>

          <span>Lien du jeu</span>
          <a
            href={GAME_URL}
            target="_blank"
            rel="noreferrer"
          >
            {GAME_URL}
          </a>
        </div>

        {shareError && (
          <p className="velo-share__error" role="alert">
            {shareError}
          </p>
        )}

        <div className="velo-share__actions">
          <button type="button" onClick={onReplay}>
            Rejouer
          </button>

          <button
            type="button"
            onClick={handleFacebookShare}
            disabled={sharingFacebook}
          >
            {sharingFacebook ? "Préparation..." : "Partager sur Facebook"}
          </button>

          <button type="button" onClick={handleCopy}>
            {copied ? "Résultat copié" : "Copier mon résultat"}
          </button>
        </div>
      </section>
    </div>
  )
}
